import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import TextStyle from '@tiptap/extension-text-style';
import { FontSize } from '../extensions/FontSize';
import { TableControls } from './TableControls';
import { FontSizeControls } from './FontSizeControls';
export const RichTextEditor = ({ content, onChange, onManualSave, placeholder = "Start writing your note...", className = "", isSaving = false, lastSaved = "" }) => {
    const [showLinkDialog, setShowLinkDialog] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                },
                bulletList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
                orderedList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-blue-600 underline hover:text-blue-800 cursor-pointer',
                },
            }),
            Placeholder.configure({
                placeholder,
            }),
            Typography,
            TextStyle.configure({
                HTMLAttributes: {
                    class: 'font-size-custom',
                },
            }),
            FontSize.configure({
                types: ['textStyle'],
            }),
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onChange(html);
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm max-w-none focus:outline-none min-h-full',
            },
            handleKeyDown: (view, event) => {
                // Handle Ctrl+S for manual save
                if ((event.ctrlKey || event.metaKey) && event.key === 's') {
                    event.preventDefault();
                    if (onManualSave) {
                        onManualSave(editor?.getHTML() || '');
                    }
                    return true;
                }
                return false;
            },
        },
    });
    // Convert JSON content to HTML if needed
    const convertContentToHtml = useCallback((content) => {
        if (!content)
            return '';
        // Handle empty JSON object case
        if (content === '{}')
            return '';
        // If content is already HTML (contains HTML tags), return as-is
        if (content.includes('<') && content.includes('>')) {
            return content;
        }
        // Try to parse as JSON (old format)
        try {
            const parsed = JSON.parse(content);
            if (parsed.type === 'doc' && parsed.content && parsed.content.length > 0) {
                // Convert JSON to HTML manually
                const convertNode = (node) => {
                    if (node.type === 'text') {
                        let text = node.text || '';
                        if (node.marks) {
                            for (const mark of node.marks) {
                                if (mark.type === 'bold')
                                    text = `<strong>${text}</strong>`;
                                if (mark.type === 'italic')
                                    text = `<em>${text}</em>`;
                                if (mark.type === 'strike')
                                    text = `<s>${text}</s>`;
                                if (mark.type === 'link')
                                    text = `<a href="${mark.attrs.href}">${text}</a>`;
                                if (mark.type === 'code')
                                    text = `<code>${text}</code>`;
                            }
                        }
                        return text;
                    }
                    if (node.type === 'paragraph') {
                        const content = node.content ? node.content.map(convertNode).join('') : '';
                        return `<p>${content}</p>`;
                    }
                    if (node.type === 'heading') {
                        const level = node.attrs?.level || 1;
                        const content = node.content ? node.content.map(convertNode).join('') : '';
                        return `<h${level}>${content}</h${level}>`;
                    }
                    if (node.type === 'bulletList') {
                        const items = node.content ? node.content.map(convertNode).join('') : '';
                        return `<ul>${items}</ul>`;
                    }
                    if (node.type === 'orderedList') {
                        const items = node.content ? node.content.map(convertNode).join('') : '';
                        return `<ol>${items}</ol>`;
                    }
                    if (node.type === 'listItem') {
                        const content = node.content ? node.content.map(convertNode).join('') : '';
                        return `<li>${content}</li>`;
                    }
                    if (node.type === 'blockquote') {
                        const content = node.content ? node.content.map(convertNode).join('') : '';
                        return `<blockquote>${content}</blockquote>`;
                    }
                    if (node.type === 'codeBlock') {
                        const content = node.content ? node.content.map(convertNode).join('') : '';
                        return `<pre><code>${content}</code></pre>`;
                    }
                    // Default: just return content if it exists
                    return node.content ? node.content.map(convertNode).join('') : '';
                };
                return parsed.content.map(convertNode).join('');
            }
            else if (parsed.type === 'doc' && (!parsed.content || parsed.content.length === 0)) {
                // Empty document
                return '';
            }
        }
        catch (error) {
            // If JSON parsing fails, treat as plain text
            return `<p>${content}</p>`;
        }
        // Fallback: treat as plain text
        return `<p>${content}</p>`;
    }, []);
    // Update editor content when prop changes (for external updates)
    useEffect(() => {
        if (editor) {
            const htmlContent = convertContentToHtml(content);
            if (htmlContent !== editor.getHTML()) {
                editor.commands.setContent(htmlContent, false);
            }
        }
    }, [content, editor, convertContentToHtml]);
    const normalizeUrl = useCallback((url) => {
        // Remove any leading/trailing whitespace
        url = url.trim();
        // If URL already has protocol, return as is
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }
        // If URL starts with www., add https://
        if (url.startsWith('www.')) {
            return `https://${url}`;
        }
        // If URL looks like a domain (contains a dot), add https://
        if (url.includes('.') && !url.includes(' ')) {
            return `https://${url}`;
        }
        // Otherwise, assume it needs https://
        return `https://${url}`;
    }, []);
    const handleAddLink = useCallback(() => {
        if (!editor)
            return;
        const { from, to } = editor.state.selection;
        const selectedText = editor.state.doc.textBetween(from, to);
        if (selectedText) {
            // If text is selected, show dialog to add URL
            setLinkUrl('');
            setShowLinkDialog(true);
        }
        else {
            // If no text selected, prompt for both text and URL
            let url = window.prompt('Enter URL:');
            const text = window.prompt('Enter link text:');
            if (url && text) {
                // Ensure URL has proper protocol
                url = normalizeUrl(url);
                editor.chain().focus().insertContent(`<a href="${url}">${text}</a>`).run();
            }
        }
    }, [editor, normalizeUrl]);
    const handleLinkSubmit = useCallback(() => {
        if (!editor || !linkUrl)
            return;
        // Normalize the URL before setting the link
        const normalizedUrl = normalizeUrl(linkUrl);
        editor.chain().focus().setLink({ href: normalizedUrl }).run();
        setShowLinkDialog(false);
        setLinkUrl('');
    }, [editor, linkUrl, normalizeUrl]);
    if (!editor) {
        return (_jsx("div", { className: `flex items-center justify-center h-32 ${className}`, children: _jsx("div", { className: "animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-600" }) }));
    }
    return (_jsxs("div", { className: "rich-text-editor-container", children: [_jsx("div", { className: "flex-shrink-0 border-b border-gray-200 p-2 bg-white", children: _jsxs("div", { className: "flex items-center gap-1 flex-wrap", children: [_jsx("button", { onClick: () => editor.chain().focus().toggleBold().run(), className: `p-2 rounded hover:bg-gray-100 transition-colors ${editor.isActive('bold') ? 'bg-purple-100 text-purple-700' : ''}`, title: "Bold (Ctrl+B)", children: _jsx("strong", { className: "text-sm", children: "B" }) }), _jsx("button", { onClick: () => editor.chain().focus().toggleItalic().run(), className: `p-2 rounded hover:bg-gray-100 transition-colors ${editor.isActive('italic') ? 'bg-purple-100 text-purple-700' : ''}`, title: "Italic (Ctrl+I)", children: _jsx("em", { className: "text-sm", children: "I" }) }), _jsx("button", { onClick: () => editor.chain().focus().toggleStrike().run(), className: `p-2 rounded hover:bg-gray-100 transition-colors ${editor.isActive('strike') ? 'bg-purple-100 text-purple-700' : ''}`, title: "Strikethrough", children: _jsx("s", { className: "text-sm", children: "S" }) }), _jsx("div", { className: "w-px h-6 bg-gray-300 mx-1" }), _jsx("button", { onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(), className: `px-2 py-1 rounded hover:bg-gray-100 text-sm font-medium transition-colors ${editor.isActive('heading', { level: 1 }) ? 'bg-purple-100 text-purple-700' : ''}`, title: "Heading 1", children: "H1" }), _jsx("button", { onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), className: `px-2 py-1 rounded hover:bg-gray-100 text-sm font-medium transition-colors ${editor.isActive('heading', { level: 2 }) ? 'bg-purple-100 text-purple-700' : ''}`, title: "Heading 2", children: "H2" }), _jsx("button", { onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(), className: `px-2 py-1 rounded hover:bg-gray-100 text-sm font-medium transition-colors ${editor.isActive('heading', { level: 3 }) ? 'bg-purple-100 text-purple-700' : ''}`, title: "Heading 3", children: "H3" }), _jsx("div", { className: "w-px h-6 bg-gray-300 mx-1" }), _jsx("button", { onClick: () => editor.chain().focus().toggleBulletList().run(), className: `p-2 rounded hover:bg-gray-100 transition-colors ${editor.isActive('bulletList') ? 'bg-purple-100 text-purple-700' : ''}`, title: "Bullet List", children: _jsx("span", { className: "text-sm", children: "\u2022" }) }), _jsx("button", { onClick: () => editor.chain().focus().toggleOrderedList().run(), className: `p-2 rounded hover:bg-gray-100 transition-colors ${editor.isActive('orderedList') ? 'bg-purple-100 text-purple-700' : ''}`, title: "Numbered List", children: _jsx("span", { className: "text-sm", children: "1." }) }), _jsx("button", { onClick: () => editor.chain().focus().toggleBlockquote().run(), className: `p-2 rounded hover:bg-gray-100 transition-colors ${editor.isActive('blockquote') ? 'bg-purple-100 text-purple-700' : ''}`, title: "Quote", children: _jsx("span", { className: "text-sm", children: "\"" }) }), _jsx("button", { onClick: () => editor.chain().focus().toggleCodeBlock().run(), className: `p-2 rounded hover:bg-gray-100 transition-colors ${editor.isActive('codeBlock') ? 'bg-purple-100 text-purple-700' : ''}`, title: "Code Block", children: _jsx("span", { className: "text-sm", children: "</>" }) }), _jsx("div", { className: "w-px h-6 bg-gray-300 mx-1" }), _jsx("button", { onClick: handleAddLink, className: `p-2 rounded hover:bg-gray-100 transition-colors ${editor.isActive('link') ? 'bg-purple-100 text-purple-700' : ''}`, title: "Add Link", children: _jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.102m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" }) }) }), _jsx("button", { onClick: () => editor.chain().focus().unsetLink().run(), className: "p-2 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed", title: "Remove Link", disabled: !editor.isActive('link'), children: _jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) }), _jsx("div", { className: "w-px h-6 bg-gray-300 mx-1" }), _jsx(TableControls, { editor: editor }), _jsx(FontSizeControls, { editor: editor }), _jsx("button", { onClick: () => editor.chain().focus().clearNodes().unsetAllMarks().run(), className: "p-2 rounded hover:bg-gray-100 transition-colors", title: "Clear Formatting", children: _jsx("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }) }), _jsx("div", { className: "w-px h-6 bg-gray-300 mx-1" }), onManualSave && (_jsx("button", { onClick: () => onManualSave(editor.getHTML()), disabled: isSaving, className: "px-3 py-1 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors", title: "Save", children: "Save" })), _jsx("div", { className: "flex items-center text-xs text-gray-500 ml-2", children: isSaving ? (_jsxs("div", { className: "flex items-center", children: [_jsx("div", { className: "animate-spin rounded-full h-3 w-3 border-t border-purple-600 mr-1" }), _jsx("span", { children: "Auto-saving..." })] })) : lastSaved ? (_jsx("span", { children: "Auto-save enabled \u2022 Press Ctrl+S to save" })) : null })] }) }), showLinkDialog && (_jsx("div", { className: "flex-shrink-0 p-3 bg-purple-50 border-b border-purple-200", children: _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "url", value: linkUrl, onChange: (e) => setLinkUrl(e.target.value), placeholder: "Enter URL (e.g., https://example.com)", className: "flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent", autoFocus: true, onKeyDown: (e) => {
                                if (e.key === 'Enter') {
                                    handleLinkSubmit();
                                }
                                else if (e.key === 'Escape') {
                                    setShowLinkDialog(false);
                                    setLinkUrl('');
                                }
                            } }), _jsx("button", { onClick: handleLinkSubmit, disabled: !linkUrl.trim(), className: "px-4 py-2 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors", children: "Add" }), _jsx("button", { onClick: () => {
                                setShowLinkDialog(false);
                                setLinkUrl('');
                            }, className: "px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-400 transition-colors", children: "Cancel" })] }) })), _jsx("div", { className: "rich-text-editor-content", children: _jsx(EditorContent, { editor: editor }) })] }));
};
